<template>
    <div>
        <div
            class="tw-flex tw-flex-col md:tw-flex-row tw-justify-center tw-items-center md:tw-items-start"
        >
            <div
                v-if="latestSurveyPeriod"
                class="tw-flex-shrink-0 tw-mb-4 md:tw-mb-0 tw-max-w-[250px]"
            >
                <div
                    class="tw-divide-x-0 tw-divide-y tw-divide-solid tw-divide-gray-100 tw-space-y-4 tw-rounded-md"
                >
                    <div class="tw-font-bold tw-text-lg">
                        {{ $t("surveys.details.latestPeriodHeading") }}
                    </div>

                    <div
                        class="tw-text-4xl tw-font-bold tw-flex tw-items-center tw-pt-4 tw-border-t-0 no-border"
                    >
                        <span>{{ latestSurveyPeriod.participationInPercent }}%</span>

                        <div
                            v-if="changeTolastPeriodInPercent"
                            v-b-popover.hover.top="
                                $t('surveys.details.inComparisonToPreviousPeriodHint')
                            "
                            class="tw-text-sm tw-inline-block tw-font-normal tw-ml-4 cursor"
                            :class="{
                                'tw-text-green-400': changeTolastPeriodInPercent > 0,
                                'tw-text-yellow-500': changeTolastPeriodInPercent < 0,
                            }"
                        >
                            <i
                                class="fal fa-fw tw-mr-1"
                                :class="{
                                    'fa-chart-line': changeTolastPeriodInPercent >= 0,
                                    'fa-chart-line-down': changeTolastPeriodInPercent < 0,
                                }"
                            />
                            {{ changeTolastPeriodInPercent }}%
                        </div>
                    </div>

                    <div class="tw-text-gray-400 tw-pt-4">
                        <i class="fal fa-calendar fa-fw tw-mr-2" />
                        {{ $t("surveys.details.periodStartedOn") }}
                        {{ latestSurveyPeriod.startDate | moment("MMM DD, YYYY") }}
                    </div>

                    <div class="tw-text-gray-400 tw-pt-4">
                        <i class="fal fa-users fa-fw tw-mr-2" />
                        {{
                            $t("surveys.details.completedBy", {
                                ratio: `${latestSurveyPeriod.doneAmount} /
                        ${latestSurveyPeriod.totalAmount}`,
                            })
                        }}
                    </div>

                    <div v-if="averageStats" class="tw-pt-4">
                        <i18n path="surveys.details.participationRateAverages">
                            <template v-slot:participationInPercent>
                                <b>{{ averageStats.participationInPercent }}%</b>
                            </template>
                            <template v-slot:doneAmount>
                                <b for="doneAmount">{{ averageStats.doneAmount }}</b>
                            </template>
                        </i18n>
                    </div>
                </div>
            </div>

            <div class="tw-flex-1">
                <div class="tw-text-right tw-text-gray-400 tw-text-sm tw-mb-4 tw-mt-8">
                    {{ $t("surveys.details.dataUpdatedAtLeastEvery24h") }}
                </div>

                <l-chart
                    :options="chartSettings.options"
                    :data="chartData"
                    :colors="['primary']"
                    height="380"
                    type="line"
                />
            </div>
        </div>
    </div>
</template>

<script>
import LChart from "@/components/LChart";

export default {
    components: {
        LChart,
    },
    props: {
        surveyPeriods: {
            type: Array,
            default: () => [],
        },
    },
    computed: {
        latestSurveyPeriod() {
            return this.surveyPeriods?.[this.surveyPeriods?.length - 1] ?? null;
        },
        changeTolastPeriodInPercent() {
            if (!Array.isArray(this.surveyPeriods) || this.surveyPeriods.length < 2) return 0;

            return (
                Math.round(
                    (this.latestSurveyPeriod.participationInPercent -
                        this.surveyPeriods?.[this.surveyPeriods?.length - 2]
                            ?.participationInPercent +
                        Number.EPSILON) *
                        100,
                ) / 100
            );
        },
        averageStats() {
            const relevantPeriods = [...this.surveyPeriods];
            if (!relevantPeriods.length) return;

            // active periods should not be considered
            if (
                this.$moment().isSameOrBefore(
                    relevantPeriods?.[relevantPeriods.length - 1]?.endDate,
                )
            ) {
                relevantPeriods.pop();
            }

            if (!relevantPeriods.length) return;

            const totals = relevantPeriods.reduce((periodA, periodB) => ({
                doneAmount: periodA.doneAmount + periodB.doneAmount,
                participationInPercent:
                    periodA.participationInPercent + periodB.participationInPercent,
            }));

            return {
                doneAmount: Math.round(totals.doneAmount / relevantPeriods.length),
                participationInPercent: Math.round(
                    totals.participationInPercent / relevantPeriods.length,
                ),
            };
        },
        chartData() {
            return {
                labels: Array.isArray(this.surveyPeriods)
                    ? this.surveyPeriods.map(period => period.startDate)
                    : [],
                datasets: [
                    {
                        label: "Participation Rate",
                        data: Array.isArray(this.surveyPeriods)
                            ? this.surveyPeriods.map(period => period.participationInPercent)
                            : [],
                        cubicInterpolationMode: "monotone",
                        fill: true,
                        lineTension: 0,
                    },
                ],
            };
        },
        chartSettings() {
            return {
                options: {
                    spanGaps: true,
                    responsive: true,
                    maintainAspectRatio: false,
                    fullWidth: true,
                    title: {
                        display: false,
                    },
                    legend: {
                        display: false,
                    },
                    tooltips: {
                        enabled: true,
                        intersect: false,
                        mode: "nearest",
                        callbacks: {
                            title: item => {
                                return this.$t(
                                    "surveys.details.participationRateChartTootlipTitle",
                                    [item?.[0]?.label],
                                );
                            },
                            label: (item, data) => {
                                return `${
                                    data?.["datasets"]?.[0]?.["data"]?.[item?.["index"]]
                                }% ${this.$t(
                                    "surveys.details.participationRateChartTooltipLabel",
                                )}`;
                            },
                            afterLabel: item => {
                                return `${this.surveyPeriods?.[item?.["index"]]?.doneAmount} / ${
                                    this.surveyPeriods?.[item?.["index"]]?.totalAmount
                                } ${this.$t("surveys.details.participationRateChartTootlip")}`;
                            },
                        },
                    },
                    hover: {
                        mode: "nearest",
                        intersect: true,
                    },
                    animation: false,
                    scales: {
                        yAxes: [
                            {
                                display: true,
                                position: "left",
                                ticks: {
                                    suggestedMax: 100,
                                    suggestedMin: 0,
                                    min: 0,
                                    beginAtZero: true,
                                    stepSize: 25,
                                    callback: function(value) {
                                        return `${value}%`;
                                    },
                                },
                                scaleLabel: {
                                    display: true,
                                },
                            },
                        ],
                        xAxes: [
                            {
                                offset: true,
                                type: "time",
                                time: {
                                    unit: "month",
                                    tooltipFormat: "ll",
                                    displayFormats: {
                                        month: "MMM, YYYY",
                                    },
                                },
                                display: true,
                                ticks: {
                                    autoSkip: true,
                                    autoSkipPadding: 16,
                                    maxTicksLimit: 20,
                                    fontColor: "#9CA3AF",
                                    maxRotation: 90,
                                    minRotation: 45,
                                },
                                gridLines: {
                                    display: false,
                                },
                            },
                        ],
                    },
                },
            };
        },
    },
};
</script>

<style scoped>
/* works once tailwind-jit gets enabled */
.tw-max-w-\[250px\] {
    max-width: 250px;
}

.no-border {
    border: none !important;
}
</style>
